import { useState, useEffect, useRef } from 'react';
import { Select, MenuItem, TextField, InputLabel, FormHelperText, FormControl } from '.';
import { useController } from 'react-hook-form';
import * as yup from 'yup';

export const selectWithDescriptionDefaultValidationSchema = ({
  entity = 'This',
  otherOptionId = null,
  labelToEnforceDescription = 'Please specify reason',
  required = true,
}) => {
  const yupShapedObject = {
    id: required ? yup.string().required(`${entity} is required`) : yup.string().notRequired(),
    description: yup.string().when('id', {
      is: (reasonId) => {
        if (!otherOptionId) {
          return false;
        }
        return reasonId === otherOptionId;
      },
      then: (schema) => schema.required(labelToEnforceDescription),
      otherwise: (schema) => schema.notRequired(),
    }),
  };

  const schema = yup.object().shape(yupShapedObject).typeError(`${entity} is required`);

  return schema;
};

const SelectWithDescription = ({ ...props }) => {
  const { field, fieldState } = useController({
    name: props.name,
    control: props.control,
  });

  const { value, onChange, ref: innerRef } = field;

  // To ensure that error works,
  // use same name in the component
  // and the yup schema validation item
  const { error } = fieldState;

  const {
    alwaysShowDescription = false,
    disabled = false,
    focusDescription = false,
    fullWidth = true,
    labelDescription = '',
    labelId = 'select-with-description-label-id',
    otherOptionDescription = 'Other',
    otherOptionId = 'other',
    otherOptionLabel = 'Other',
    options = [
      { id: 'option-1', Description: 'Option 1', isJsx: false },
      { id: 'option-2', Description: 'Option 2', isJsx: false },
      { id: 'option-3', Description: <span style={{ color: 'darkred' }}>Option 3</span>, isJsx: true },
    ],
    placeholder = '',
    showOtherOption = false,
    ...otherProps
  } = props;

  const [isLastOptionSelected, setIsLastOptionSelected] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState('');
  const inputDescriptionRef = useRef(null);

  useEffect(() => {
    if (focusDescription && isLastOptionSelected && inputDescriptionRef.current) {
      inputDescriptionRef.current.focus();
    }
  }, [focusDescription, isLastOptionSelected]);

  function handleSelectChange(event) {
    const newSelectId = event.target.value;
    const userSelectedOtherOption = newSelectId === otherOptionId;
    setIsLastOptionSelected(userSelectedOtherOption);
    onChange({ id: newSelectId, description: descriptionValue.trim() || '' });
  }

  function handleDescriptionChange(event) {
    const newValue = event.target.value;
    setDescriptionValue(newValue);

    if (value?.id) {
      onChange({ id: value?.id, description: newValue });
    }
  }

  return (
    <FormControl fullWidth={fullWidth}>
      <InputLabel id={labelId}>{labelDescription}</InputLabel>
      <Select
        disabled={disabled}
        displayEmpty
        fullWidth
        label={labelDescription}
        labelId={labelId}
        onChange={handleSelectChange}
        ref={innerRef}
        value={value?.id || ''}
        {...otherProps}
      >
        {placeholder ? (
          <MenuItem disabled value="">
            <span style={{ opacity: 0.6 }}>{placeholder}</span>
          </MenuItem>
        ) : null}

        {options.map((option) => {
          return (
            <MenuItem key={option.id} value={option.id}>
              {option.isJsx ? (
                <>
                  <option.Description />
                </>
              ) : (
                <span>{option.Description}</span>
              )}
            </MenuItem>
          );
        })}

        {showOtherOption ? (
          <MenuItem value={otherOptionId}>
            <span>{otherOptionDescription}</span>
          </MenuItem>
        ) : null}
      </Select>

      {error?.message ? (
        <FormHelperText error>{error.message}</FormHelperText>
      ) : error?.id?.message ? (
        <FormHelperText error>{error.id.message}</FormHelperText>
      ) : null}

      {(isLastOptionSelected && showOtherOption) || alwaysShowDescription ? (
        <TextField
          inputRef={inputDescriptionRef}
          sx={{ marginTop: 2, width: '100%' }}
          id={`${labelId}-other`}
          label={otherOptionLabel}
          value={descriptionValue}
          onChange={handleDescriptionChange}
        />
      ) : null}

      {error?.description?.message ? <FormHelperText error>{error.description.message}</FormHelperText> : null}
    </FormControl>
  );
};

export default SelectWithDescription;
